import { AuthFetchWithResponse } from 'src/Types/api'
import { ApiResult } from 'src/Libs/api'
import { StatusCodes } from 'src/Libs/http-status-codes'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import { handleFetchError } from 'src/Libs/api/utils'
import { ChargingScheduleDto } from '../dto'
import { ChargingScheduleModel } from '../models'
import { transformChargingSchedulesDtoToModel } from '../transformer'

/**
 * send request to create a charging schedule (onetime / recurrent)
 */
export const createChargingSchedule = async (
  vehicleId: number,
  installationId: number,
  data: Omit<ChargingScheduleModel, 'id'>,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<boolean, AuthFetchError | Error>> => {
  try {
    const response = await authFetch<string>({
      path: `vehicles/installations/${installationId}/vehicles/${vehicleId}/charging-schedules`,
      method: 'POST',
      payload: { ...data },
      endpoint: process.env.REACT_APP_VEHICLES_ENDPOINT,
    })

    if (response.status === StatusCodes.CREATED) {
      return new ApiResult<boolean, Error | AuthFetchError>(true)
    }

    return new ApiResult<boolean, Error | AuthFetchError>(null, true)
  } catch (error) {
    return handleFetchError<boolean>(error)
  }
}

/**
 * send request to fetch charging schedules (onetime / recurrent)
 */
export const fetchChargingSchedules = async (
  vehicleId: number,
  installationId: number,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<ChargingScheduleModel[], AuthFetchError | Error>> => {
  try {
    const response = await authFetch<ChargingScheduleDto[]>({
      path: `vehicles/installations/${installationId}/vehicles/${vehicleId}/charging-schedules`,
      method: 'GET',
      endpoint: process.env.REACT_APP_VEHICLES_ENDPOINT,
    })

    if (response.status === StatusCodes.OK) {
      const chargingSchedules = transformChargingSchedulesDtoToModel(response.data)
      return new ApiResult<ChargingScheduleModel[], Error | AuthFetchError>(chargingSchedules)
    }

    return new ApiResult<ChargingScheduleModel[], Error | AuthFetchError>(null, true)
  } catch (error) {
    return handleFetchError<ChargingScheduleModel[]>(error)
  }
}

/**
 * send request to update a charging schedule (onetime / recurrent)
 */
export const updateChargingSchedule = async (
  vehicleId: number,
  installationId: number,
  scheduleId: number,
  data: Omit<ChargingScheduleModel, 'id'>,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<boolean, AuthFetchError | Error>> => {
  try {
    const response = await authFetch<string>({
      path: `vehicles/installations/${installationId}/vehicles/${vehicleId}/charging-schedules/${scheduleId}`,
      method: 'PUT',
      payload: data,
      endpoint: process.env.REACT_APP_VEHICLES_ENDPOINT,
    })

    if (response.status === StatusCodes.NO_CONTENT) {
      return new ApiResult<boolean, Error | AuthFetchError>(true)
    }

    return new ApiResult<boolean, Error | AuthFetchError>(null, true)
  } catch (error) {
    return handleFetchError<boolean>(error)
  }
}

export const deleteChargingSchedule = async (
  vehicleId: number,
  installationId: number,
  scheduleId: number,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<boolean, AuthFetchError | Error>> => {
  try {
    const response = await authFetch<string>({
      path: `vehicles/installations/${installationId}/vehicles/${vehicleId}/charging-schedules/${scheduleId}`,
      method: 'DELETE',
      endpoint: process.env.REACT_APP_VEHICLES_ENDPOINT,
    })

    if (response.status === StatusCodes.NO_CONTENT) {
      return new ApiResult<boolean, Error | AuthFetchError>(true)
    }

    return new ApiResult<boolean, Error | AuthFetchError>(null, true)
  } catch (error) {
    return handleFetchError<boolean>(error)
  }
}
